import React, { useState, useEffect, useRef } from "react";
import { gql } from "apollo-boost";
import StudentAnalysisTab from "./StudentAnalysisTab";
import QuestionAnalysisTab from "./QuestionAnalysisTab";

const GET_EXAM_DETAIL = gql`
  query ($exam_id: Int!) {
    getExamResultDetail(exam_id: $exam_id) {
      id
      name
      className
      date
      appeared
      avgPct
      highPct
      lowPct
      passPct
      totalQuestions
      maxMarks
      marksPerCorrect
      marksPerWrong
      avgMarks
      highMarks
      lowMarks
      sections
      subjectNames
      subjectAvg {
        subject
        pct
        avgMarks
        maxMarks
      }
      students {
        rank
        name
        mobile
        sectionName
        studentUserId
        correct
        wrong
        totalQuestions
        pct
        percentile
        marks
        correctMarks
        negativeMarks
        maxMarks
        subjects {
          subject
          correct
          wrong
          total
          pct
          marks
          maxMarks
        }
      }
    }
  }
`;

const GET_EXAM_QUESTION_ANALYSIS = gql`
  query ($exam_id: Int!, $section: String) {
    getExamQuestionAnalysis(exam_id: $exam_id, section: $section) {
      takers
      totalQuestions
      totalCorrect
      totalWrong
      totalUnattempted
      correctPct
      wrongPct
      unattemptedPct
      errorPct
      okQuestions
      warnQuestions
      criticalQuestions
      questions {
        slno
        questionId
        subject
        chapter
        topic
        question
        questionText
        takers
        correct
        wrong
        unattempted
        correctPct
        wrongPct
        unattemptedPct
        errorPct
        option1
        option2
        option3
        option4
        answer
        explanation
        option1Count
        option2Count
        option3Count
        option4Count
        options {
          optionIndex
          optionKey
          optionText
          count
          pct
          isCorrect
        }
      }
    }
  }
`;

// Module-level cache
const examDetailCache = {};
const questionAnalysisCache = {};

function ExamDetailsModal({ exam, client, onClose, authorizedClassNames, authorizedSectionNames, authorizedSubjectNames, orgProfile }) {
    const [activeTab, setActiveTab] = useState("student");

    const [examDetail, setExamDetail] = useState(examDetailCache[exam.id] || null);
    const [examDetailLoading, setExamDetailLoading] = useState(!examDetailCache[exam.id]);
    const [examDetailError, setExamDetailError] = useState(false);

    const [questionData, setQuestionData] = useState(questionAnalysisCache[exam.id] || null);
    const [questionLoading, setQuestionLoading] = useState(false);
    const [questionError, setQuestionError] = useState(false);

    const questionFetchedRef = useRef(Boolean(questionAnalysisCache[exam.id]));

    useEffect(() => {
        if (examDetailCache[exam.id]) return;
        let cancelled = false;
        setExamDetailLoading(true);
        setExamDetailError(false);
        client
            .query({ query: GET_EXAM_DETAIL, variables: { exam_id: Number(exam.id) }, fetchPolicy: "network-only" })
            .then((result) => {
                if (cancelled) return;
                const detail = result.data.getExamResultDetail || null;
                examDetailCache[exam.id] = detail;
                setExamDetail(detail);
                setExamDetailLoading(false);
            })
            .catch((err) => {
                console.error("ExamDetailsModal getExamResultDetail error", err);
                if (cancelled) return;
                setExamDetailError(true);
                setExamDetailLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exam.id]);

    const openQuestionTab = () => {
        setActiveTab("question");
        if (questionFetchedRef.current) return;
        questionFetchedRef.current = true;
        setQuestionLoading(true);
        setQuestionError(false);
        client
            .query({
                query: GET_EXAM_QUESTION_ANALYSIS,
                variables: { exam_id: Number(exam.id), section: "ALL" },
                fetchPolicy: "network-only",
            })
            .then((result) => {
                const data = result.data.getExamQuestionAnalysis || null;
                questionAnalysisCache[exam.id] = data;
                setQuestionData(data);
                setQuestionLoading(false);
            })
            .catch((err) => {
                console.error("ExamDetailsModal getExamQuestionAnalysis error", err);
                setQuestionError(true);
                setQuestionLoading(false);
            });
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "#f8fafc", zIndex: 1040, display: "flex", flexDirection: "column" }}>
            {/* Header Bar */}
            <div
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                    gap: 16,
                    padding: "16px 24px",
                    background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #312e81 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 20px rgba(15, 23, 42, 0.15)",
                }}
            >
                <div className="d-flex align-items-center" style={{ gap: 14 }}>
                    <div
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: "rgba(255, 255, 255, 0.1)",
                            backdropFilter: "blur(8px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid rgba(255, 255, 255, 0.15)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        <i className="fal fa-chart-line" style={{ fontSize: 20, color: "#a5b4fc" }} />
                    </div>
                    <div>
                        <h5 className="font-weight-bold mb-1" style={{ color: "#fff", fontSize: 18, letterSpacing: "-0.2px" }}>
                            {exam.name}
                        </h5>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                            {exam.className && (
                                <span style={{ background: "rgba(165, 180, 252, 0.2)", color: "#c7d2fe", border: "1px solid rgba(165, 180, 252, 0.3)", padding: "2px 10px", borderRadius: 12, fontSize: 11.5, fontWeight: 700 }}>
                                    🎓 {exam.className}
                                </span>
                            )}
                            {exam.totalStudents > 0 && (
                                <span style={{ background: "rgba(255, 255, 255, 0.1)", color: "#e0e7ff", border: "1px solid rgba(255, 255, 255, 0.15)", padding: "2px 10px", borderRadius: 12, fontSize: 11.5, fontWeight: 600 }}>
                                    👥 {exam.totalStudents} Students
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center" style={{ gap: 14 }}>
                    {/* Segmented Tab Switcher */}
                    <div style={{ background: "rgba(15, 23, 42, 0.6)", borderRadius: 12, padding: 4, display: "flex", gap: 4, border: "1px solid rgba(255, 255, 255, 0.1)" }}>
                        <button
                            type="button"
                            style={{
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 16px",
                                fontSize: 12.5,
                                fontWeight: 700,
                                transition: "all 0.2s ease",
                                background: activeTab === "student" ? "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)" : "transparent",
                                color: activeTab === "student" ? "#fff" : "#94a3b8",
                                boxShadow: activeTab === "student" ? "0 4px 12px rgba(79, 70, 229, 0.3)" : "none",
                            }}
                            onClick={() => setActiveTab("student")}
                        >
                            <i className="fal fa-user-graduate me-1" /> Student Analysis
                        </button>
                        <button
                            type="button"
                            style={{
                                border: "none",
                                borderRadius: 8,
                                padding: "6px 16px",
                                fontSize: 12.5,
                                fontWeight: 700,
                                transition: "all 0.2s ease",
                                background: activeTab === "question" ? "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)" : "transparent",
                                color: activeTab === "question" ? "#fff" : "#94a3b8",
                                boxShadow: activeTab === "question" ? "0 4px 12px rgba(79, 70, 229, 0.3)" : "none",
                            }}
                            onClick={openQuestionTab}
                        >
                            <i className="fal fa-question-circle me-1" /> Question Analysis
                        </button>
                    </div>

                    {/* Close Button */}
                    <button
                        type="button"
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            background: "rgba(255, 255, 255, 0.1)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                        }}
                        onClick={onClose}
                        title="Close Modal"
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#ef4444")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")}
                    >
                        <i className="fal fa-times" />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div style={{ flex: 1, overflowY: "auto", padding: 24, background: "#f8fafc" }}>
                {activeTab === "student" ? (
                    examDetailLoading ? (
                        <div>
                            {[...Array(4)].map((_, idx) => (
                                <div key={idx} className="lecture-exam-shim w-100 mb-3" style={{ height: 90 }} />
                            ))}
                        </div>
                    ) : examDetailError ? (
                        <p className="text-danger text-center py-5">Unable to load student analysis for this exam.</p>
                    ) : !examDetail ? (
                        <p className="text-muted text-center py-5">No details available for this exam.</p>
                    ) : (
                        <StudentAnalysisTab
                            examDetail={examDetail}
                            listMeta={exam}
                            authorizedClassNames={authorizedClassNames}
                            authorizedSectionNames={authorizedSectionNames}
                            authorizedSubjectNames={authorizedSubjectNames}
                            orgProfile={orgProfile}
                        />
                    )
                ) : (
                    <QuestionAnalysisTab
                        data={questionData}
                        loading={questionLoading}
                        error={questionError}
                        authorizedSubjectNames={authorizedSubjectNames}
                    />
                )}
            </div>
        </div>
    );
}

export default ExamDetailsModal;
